import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

/**
 * Top-level event fields allowed to leave the browser. Everything else (message, logentry, request,
 * user, extra, breadcrumbs, modules, server_name, transaction, ...) is dropped. This is an allowlist so
 * that fields added by future SDK versions or integrations are excluded until reviewed.
 */
const ALLOWED_EVENT_KEYS = new Set<string>([
  'event_id',
  'timestamp',
  'platform',
  'level',
  'release',
  'dist',
  'environment',
  'sdk',
  'tags',
  'fingerprint',
  'exception',
  'contexts',
  'debug_meta',
  'type',
  // Internal to the SDK; removed before the envelope is sent.
  'sdkProcessingMetadata',
]);

/** Browser/OS/device come from the user agent; trace links events. Culture (locale/timezone) is dropped. */
const ALLOWED_CONTEXT_KEYS = new Set<string>(['browser', 'os', 'device', 'trace']);

/** Page URLs (e.g. frames from inline scripts) can carry query strings or hashes. */
const stripUrlParams = (url: string | undefined): string | undefined => url?.replace(/[?#].*$/, '');

/**
 * Reduces an event to error type, stack trace, and environment metadata. The registration forms
 * collect PHI, which can surface in exception messages (e.g. echoed GraphQL validation errors),
 * request URLs, breadcrumbs, or attached data, so none of those are sent.
 */
export const scrubEvent = (event: ErrorEvent): ErrorEvent => {
  Object.keys(event).forEach((key) => {
    if (!ALLOWED_EVENT_KEYS.has(key)) delete event[key as keyof ErrorEvent];
  });

  if (event.contexts) {
    Object.keys(event.contexts).forEach((key) => {
      if (!ALLOWED_CONTEXT_KEYS.has(key)) delete event.contexts?.[key];
    });
  }

  // Stack traces and error types remain available for grouping and source-map-backed debugging.
  event.exception?.values?.forEach((exception) => {
    delete exception.value;
    if (exception.mechanism) delete exception.mechanism.data;
    exception.stacktrace?.frames?.forEach((frame) => {
      frame.filename = stripUrlParams(frame.filename);
      frame.abs_path = stripUrlParams(frame.abs_path);
      delete frame.vars;
      delete frame.pre_context;
      delete frame.context_line;
      delete frame.post_context;
    });
  });

  return event;
};

/**
 * Initializes client-side error reporting when a Sentry DSN is provided at build time.
 *
 * The /registration forms collect PHI. Only error types, stack traces, environment metadata, and
 * code-defined tags are sent. Never pass form values or server responses to Sentry (tags, contexts,
 * messages), and do not relax these settings without privacy/compliance review.
 */
export const initSentry = (): void => {
  const dsn = process.env.GATSBY_SENTRY_DSN;

  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: process.env.GATSBY_ENVIRONMENT ?? process.env.NODE_ENV,
    release: process.env.GATSBY_APP_COMMIT
      ? `start.boulder.care@${process.env.GATSBY_APP_COMMIT}`
      : undefined,
    // SDK v11 collects user info by default, which tells Sentry to infer the client IP address on
    // events and sessions. Turn off every category of collected data.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      stackFrameVariables: false,
      frameContextLines: 0,
    },
    // BrowserSession sends separate session envelopes that do not go through beforeSend. Keep
    // only the default integrations needed to capture and scrub error events.
    integrations: (integrations) =>
      integrations.filter((integration) => integration.name !== 'BrowserSession'),
    // Client reports are separate telemetry envelopes (for example, discarded-event counts),
    // rather than scrubbed error events.
    sendClientReports: false,
    // Do not retain navigation, console, network, or UI breadcrumbs.
    maxBreadcrumbs: 0,
    beforeSend: scrubEvent,
    // Tracing is not enabled; drop any transaction defensively since it would include page URLs.
    beforeSendTransaction: () => null,
  });
};
